<template>
  <button
    class="btn"
    :class="[
      variantClasses,
      sizeClasses,
      floatingClasses,
      customClass,
      {
        'opacity-50 cursor-not-allowed': disabled,
        'btn-floating': floating,
        'btn-form': form,
        'btn-loading': loading
      }
    ]"
    :disabled="disabled || loading"
    v-bind="$attrs"
    @click="handleClick"
  >
    <slot />
  </button>
</template>
<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: val => ['primary', 'secondary', 'ghost', 'outline', 'danger'].includes(val)
  },
  size: {
    type: String,
    default: 'md',
    validator: val => ['sm', 'md', 'lg', 'small', 'icon'].includes(val)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  form: {
    type: Boolean,
    default: false
  },
  floating: {
    type: Boolean,
    default: false
  },
  customClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['click'])

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'bg-primary text-on-primary border-primary hover:bg-primary-container hover:border-primary-container'
    case 'secondary':
      return 'bg-transparent text-tertiary border-tertiary hover:bg-tertiary-fixed hover:text-tertiary'
    case 'outline':
      return 'bg-transparent text-primary border-primary hover:bg-primary hover:text-on-primary'
    case 'danger':
      return 'bg-error text-on-error border-error hover:bg-error-container hover:text-on-error-container'
    case 'ghost':
      return 'bg-transparent text-on-surface-variant border-transparent shadow-none hover:bg-surface-container-high hover:text-primary'
    default:
      return ''
  }
})

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
    case 'small':
      return 'btn-sm'
    case 'icon':
      return 'btn-icon'
    case 'lg':
      return 'btn-lg'
    case 'md':
    default:
      return '' // Default size is handled by .btn base class
  }
})

const floatingClasses = computed(() => {
  if (props.floating) {
    return 'shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0 active:scale-95'
  }
  return ''
})

const handleClick = event => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>
